import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauth, obj = user
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data);
            return { ok: true };
        } catch (e) {
            const detail = e.response?.data?.detail;
            const message = formatApiErrorDetail(detail);
            return {
                ok: false,
                error: message || e.response?.data?.message || e.message || "Login failed",
            };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            setUser(data);
            return { ok: true };
        } catch (e) {
            const detail = e.response?.data?.detail;
            const message = formatApiErrorDetail(detail);
            return {
                ok: false,
                error: message || e.response?.data?.message || e.message || "Registration failed",
            };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* noop */ }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{
            user, loading, login, register, logout, refetch: fetchMe,
            isAdmin: user && user.role === "admin",
            role: user?.role,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
